import { Link, Progress } from '@backstage/core-components'
import { Box, makeStyles, Theme, Typography } from '@material-ui/core'

import { WorkflowJob } from '../../../../api'
import { useNow } from '../../../../state'
import { durationHumanized } from '../../../../util'
import { StatusBadge } from '../StatusBadge'

const useStyles = makeStyles<Theme>(theme => ({
  container: {
    padding: theme.spacing(1, 4, 1.5, 6),
  },
  header: {
    color: theme.palette.text.secondary,
    textTransform: 'uppercase',
    fontSize: 11,
    letterSpacing: 0.5,
    marginBottom: theme.spacing(0.5),
  },
  jobRow: {
    display: 'grid',
    gridTemplateColumns: '120px minmax(180px, 1fr) 80px 110px',
    alignItems: 'center',
    columnGap: theme.spacing(2),
    padding: theme.spacing(0.5, 0),
  },
  jobName: {
    fontWeight: 500,
  },
  empty: {
    color: theme.palette.text.secondary,
    fontStyle: 'italic',
    padding: theme.spacing(1, 0),
  },
}))

type Props = {
  jobs: WorkflowJob[]
  loading: boolean
  error?: Error
}

const formatJobDuration = (start: string | null, stop: string | null, now: Date): string => {
  if (!start) return '—'
  return durationHumanized(start, stop ?? now.toISOString()) || '—'
}

export const JobList = ({ jobs, loading, error }: Props) => {
  const classes = useStyles()
  const now = useNow()

  if (loading) {
    return (
      <Box className={classes.container}>
        <Progress />
      </Box>
    )
  }

  if (error) {
    return (
      <Box className={classes.container}>
        <Typography color="error" variant="body2">
          Failed to load jobs: {error.message}
        </Typography>
      </Box>
    )
  }

  if (jobs.length === 0) {
    return (
      <Box className={classes.container}>
        <Typography className={classes.empty} variant="body2">
          No jobs found for this workflow.
        </Typography>
      </Box>
    )
  }

  return (
    <Box className={classes.container}>
      <div className={classes.header}>Jobs</div>
      {jobs.map(job => (
        <div key={job.id} className={classes.jobRow}>
          <StatusBadge status={job.status} size="sm" />
          <Typography className={classes.jobName} variant="body2">
            {job.job_number ? <Link to={`${job.job_number}`}>{job.name}</Link> : job.name}
          </Typography>
          <Typography variant="body2" color="textSecondary">
            {job.job_number ? `#${job.job_number}` : job.type === 'approval' ? 'approval' : ''}
          </Typography>
          <Typography variant="body2" color="textSecondary">
            {formatJobDuration(job.started_at, job.stopped_at, now)}
          </Typography>
        </div>
      ))}
    </Box>
  )
}
